import { useState } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import type { BudgetPool, CampaignRow, PoolCampaign } from '@/api/client'
import { useBudgetPools, useCreateBudgetPool, useDeleteBudgetPool, useUpdateBudgetPool } from '@/lib/queries'
import { count, usd } from '@/lib/money'
import { COST_REPORT_LAG_HOURS, overshootUSD, poolPercent, poolPeriod, usdTotal } from '@/lib/pools'
import { statusOf } from '@/lib/status'
import { cn } from '@/lib/utils'
import {
  Button,
  ConfirmDialog,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  IconButton,
  Input,
  Money,
  Skeleton,
  StatusPill,
  errorText,
  useToast,
  type Column,
} from '@/ui'

const today = () => {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** "$12.40 / $30" with a bar. The bar's colour follows the state, and the
 *  numbers are always there, so the colour never carries the meaning alone. */
function PoolBar({ pool }: { pool: BudgetPool }) {
  const pct = poolPercent(pool.spent_usd, pool.total_usd)
  const fill = pool.status === 'CAPPED' ? 'bg-status-deadline' : pool.status === 'ENDED' ? 'bg-muted-foreground' : pct >= 80 ? 'bg-status-publishing' : 'bg-status-published'
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 tabular-nums">
        <span className="text-body font-semibold">
          {usd(pool.spent_usd)} <span className="font-normal text-muted-foreground">/ {usdTotal(pool.total_usd)}</span>
        </span>
        <span className="text-table text-muted-foreground">
          남은 금액 <span className="text-foreground">{usd(pool.remaining_usd)}</span>
        </span>
      </div>
      <div
        role="progressbar"
        aria-label={`${pool.name} 총액 사용`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct)}
        className="mt-1.5 h-2 overflow-hidden rounded-full bg-muted"
      >
        <div className={cn('h-full rounded-full', fill)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

/** The members: spend, impressions and installs inside the pool's window. */
function MemberTable({ pool }: { pool: BudgetPool }) {
  const columns: Column<PoolCampaign>[] = [
    {
      key: 'name',
      header: '캠페인',
      card: 'title',
      cell: (c) => (
        <div className="min-w-0">
          <div className="max-w-64 truncate font-medium" title={c.name}>
            {c.name}
          </div>
          <div className="text-meta text-muted-foreground">
            {statusOf('network', c.network ?? 'google').label} · {c.app_name || '앱 연결 없음'}
          </div>
        </div>
      ),
    },
    { key: 'status', header: '상태', card: 'meta', cell: (c) => <StatusPill domain="campaign" status={c.status} /> },
    { key: 'spent', header: '지출', align: 'right', card: 'hidden', cell: (c) => <Money value={c.spent_usd} /> },
    { key: 'imp', header: '노출', align: 'right', card: 'hidden', cell: (c) => <span className="tabular-nums">{count(c.impressions)}</span> },
    { key: 'conv', header: '설치', align: 'right', card: 'hidden', cell: (c) => <span className="tabular-nums">{count(c.conversions)}</span> },
    {
      key: 'figures',
      header: '지출 · 노출 · 설치',
      cardOnly: true,
      card: 'sub',
      cell: (c) => (
        <span className="block tabular-nums">
          지출 <Money value={c.spent_usd} className="text-foreground" />
          {' · '}노출 <span className="text-foreground">{count(c.impressions)}</span>
          {' · '}설치 <span className="text-foreground">{count(c.conversions)}</span>
        </span>
      ),
    },
  ]
  return <DataTable label={`${pool.name} 소속 캠페인`} columns={columns} rows={pool.campaigns} rowKey={(c) => c.id} />
}

function PoolCard({ pool, onEdit, onDelete }: { pool: BudgetPool; onEdit: () => void; onDelete: () => void }) {
  const over = overshootUSD(pool.daily_budget_usd_sum)
  return (
    <li className="rounded-lg border border-border bg-card">
      <div className="space-y-3 px-4 py-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h3 className="min-w-0 truncate text-body font-semibold" title={pool.name}>
            {pool.name}
          </h3>
          <StatusPill domain="pool" status={pool.status} />
          <span className="text-meta text-muted-foreground">{poolPeriod(pool.start_date, pool.end_date)}</span>
          <span className="ml-auto inline-flex gap-1">
            <IconButton icon={Pencil} size="sm" label={`${pool.name} 수정`} onClick={onEdit} />
            <IconButton icon={Trash2} size="sm" label={`${pool.name} 삭제`} onClick={onDelete} />
          </span>
        </div>
        <PoolBar pool={pool} />
        {pool.status === 'CAPPED' ? (
          <p className="text-meta text-muted-foreground">
            총액을 모두 써서 소속 캠페인을 일시정지했다. 다시 돌리려면 총액을 올린 뒤 캠페인을 직접 켠다.
          </p>
        ) : pool.status === 'ENDED' ? (
          <p className="text-meta text-muted-foreground">
            기간이 끝나 소속 캠페인을 일시정지했다. 다시 돌리려면 종료일을 늦춘 뒤 캠페인을 직접 켠다.
          </p>
        ) : over > 0 ? (
          <p className="text-meta text-muted-foreground">
            보고 지연으로 약 {usd(over)} 넘을 수 있다 · 운영 중 일 예산 합 {usd(pool.daily_budget_usd_sum)} × 지연 {COST_REPORT_LAG_HOURS}시간
          </p>
        ) : null}
      </div>
      {pool.campaigns.length === 0 ? (
        <p className="border-t border-border px-4 py-3 text-table text-muted-foreground">소속 캠페인 없음 — 아래 표의 “묶음” 칸에서 넣는다.</p>
      ) : (
        <div className="border-t border-border">
          <MemberTable pool={pool} />
        </div>
      )}
    </li>
  )
}

// ── Create / edit ───────────────────────────────────────────────────────────

function PoolDialog({
  open,
  pool,
  campaigns,
  onClose,
}: {
  open: boolean
  /** The pool being edited; null = a new one. */
  pool: BudgetPool | null
  campaigns: CampaignRow[]
  onClose: () => void
}) {
  const create = useCreateBudgetPool()
  const update = useUpdateBudgetPool()
  const allPools = useBudgetPools().data
  const toast = useToast()
  const [name, setName] = useState('')
  const [total, setTotal] = useState('')
  const [start, setStart] = useState(today())
  const [end, setEnd] = useState('')
  const [picked, setPicked] = useState<number[]>([])
  const [err, setErr] = useState<string | null>(null)
  // Fill the form whenever the dialog opens for another pool (or a new one).
  const [seen, setSeen] = useState<string | null>(null)
  const key = open ? (pool ? `edit-${pool.id}` : 'new') : null
  if (key !== seen) {
    setSeen(key)
    if (key) {
      setName(pool?.name ?? '')
      setTotal(pool ? String(pool.total_usd) : '')
      setStart(pool?.start_date ?? today())
      setEnd(pool?.end_date ?? '')
      setPicked([])
      setErr(null)
    }
  }

  const totalN = Number(total)
  const endOK = end === '' || end >= start
  const valid = name.trim() !== '' && Number.isFinite(totalN) && totalN > 0 && /^\d{4}-\d{2}-\d{2}$/.test(start) && endOK
  const busy = create.isPending || update.isPending
  const raising = !!pool && pool.status !== 'ACTIVE' && totalN > pool.spent_usd && (end === '' || end >= today())

  async function submit() {
    setErr(null)
    try {
      if (pool) {
        await update.mutateAsync({ id: pool.id, body: { name: name.trim(), total_usd: totalN, end_date: end } })
        toast.success(`묶음 "${name.trim()}"을 고쳤다`)
      } else {
        await create.mutateAsync({ name: name.trim(), total_usd: totalN, start_date: start, end_date: end || undefined, campaign_ids: picked })
        toast.success(`묶음 "${name.trim()}"을 만들었다`)
      }
      onClose()
    } catch (e) {
      setErr(errorText(e))
    }
  }

  const toggle = (id: number) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))
  const poolName = (id?: number | null) => {
    const p = allPools?.find((x) => x.id === id)
    return p ? `“${p.name}”에서 옮겨 온다` : ''
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={pool ? '예산 묶음 수정' : '예산 묶음 만들기'}
      description="여러 캠페인이 함께 쓰는 총 예산. 소속 캠페인의 지출이 총액에 닿거나 종료일이 지나면 다음 동기화 때 전부 일시정지한다. 켜는 일은 하지 않는다."
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button variant="primary" disabled={!valid} loading={busy} onClick={submit}>
            {pool ? '저장' : '만들기'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="이름" required>
          <Input placeholder="10월 설치 테스트" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="총액 ($)"
            required
            hint={raising ? '이대로 저장하면 묶음이 다시 집행 중이 된다. 캠페인은 켜지지 않는다.' : pool ? `지금까지 ${usd(pool.spent_usd)} 썼다` : '이 기간 동안 쓸 수 있는 전체 금액'}
          >
            <Input inputMode="decimal" placeholder="30" value={total} onChange={(e) => setTotal(e.target.value)} />
          </Field>
          <Field label="시작일" required hint={pool ? '시작일은 바꾸지 않는다' : '이 날부터의 지출만 센다'}>
            <Input type="date" value={start} disabled={!!pool} onChange={(e) => setStart(e.target.value)} />
          </Field>
        </div>
        <Field label="종료일" hint="비우면 끝 없음. 이 날 이후의 지출은 세지 않는다" error={endOK ? undefined : '시작일 이후여야 한다'}>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
        </Field>
        {!pool && (
          <fieldset>
            <legend className="mb-1 text-table font-medium">소속 캠페인</legend>
            {campaigns.length === 0 ? (
              <p className="text-table text-muted-foreground">캠페인이 없다. 만든 뒤 표에서 넣어도 된다.</p>
            ) : (
              <ul className="max-h-48 divide-y divide-border overflow-y-auto rounded-md border border-border">
                {campaigns.map((c) => (
                  <li key={c.id}>
                    <label className="flex cursor-pointer items-center gap-2 px-3 py-2">
                      <input type="checkbox" className="h-4 w-4" checked={picked.includes(c.id)} onChange={() => toggle(c.id)} />
                      <span className="min-w-0 flex-1 truncate text-body">{c.name}</span>
                      <span className="text-meta text-muted-foreground">
                        {statusOf('network', c.network ?? 'google').label}
                        {c.pool_id ? ` · ${poolName(c.pool_id)}` : c.app_name ? ` · ${c.app_name}` : ''}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </fieldset>
        )}
        {err && <p role="alert" className="whitespace-pre-wrap break-words font-mono text-meta text-status-failed">{err}</p>}
      </div>
    </Dialog>
  )
}

// ── Section ─────────────────────────────────────────────────────────────────

export function BudgetPoolsSection({ campaigns }: { campaigns: CampaignRow[] }) {
  const pools = useBudgetPools()
  const del = useDeleteBudgetPool()
  const toast = useToast()
  const [dialog, setDialog] = useState<{ pool: BudgetPool | null } | null>(null)
  const [removing, setRemoving] = useState<BudgetPool | null>(null)
  const list = pools.data

  async function remove() {
    if (!removing) return
    try {
      await del.mutateAsync(removing.id)
      toast.success(`묶음 "${removing.name}"을 지웠다. 캠페인은 그대로 둔다`)
    } catch (e) {
      toast.error(`묶음을 지우지 못했다: ${errorText(e)}`)
    }
    setRemoving(null)
  }

  return (
    <section aria-label="예산 묶음">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-section font-semibold">
          예산 묶음
          {list && list.length > 0 && <span className="ml-2 text-table font-normal text-muted-foreground">{list.length}개</span>}
        </h2>
        <Button size="sm" icon={Plus} onClick={() => setDialog({ pool: null })}>
          묶음 만들기
        </Button>
      </div>
      {pools.isLoading ? (
        <div className="rounded-lg border border-border bg-card p-4">
          <Skeleton className="h-4 w-64 max-w-full" />
        </div>
      ) : pools.error ? (
        <p className="rounded-lg border border-border bg-card px-4 py-3 font-mono text-meta text-status-failed">{errorText(pools.error)}</p>
      ) : !list || list.length === 0 ? (
        <div className="rounded-lg border border-border bg-card">
          <EmptyState
            title="예산 묶음이 없다"
            description="Google은 앱 캠페인에 총 예산을 주지 않는다. 여러 캠페인의 총액을 묶어 두면 닿을 때 콘솔이 모두 일시정지한다."
            actions={
              <Button variant="primary" icon={Plus} onClick={() => setDialog({ pool: null })}>
                묶음 만들기
              </Button>
            }
          />
        </div>
      ) : (
        <ul className="space-y-3">
          {list.map((p) => (
            <PoolCard key={p.id} pool={p} onEdit={() => setDialog({ pool: p })} onDelete={() => setRemoving(p)} />
          ))}
        </ul>
      )}
      <PoolDialog open={!!dialog} pool={dialog?.pool ?? null} campaigns={campaigns} onClose={() => setDialog(null)} />
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={removing ? `"${removing.name}" 묶음을 지울까` : ''}
        description="소속만 풀고 캠페인은 건드리지 않는다. 지금 돌고 있는 캠페인은 계속 돈다 — 총액을 지키는 장치가 사라진다."
        confirmLabel="묶음 지우기"
        loading={del.isPending}
        onConfirm={remove}
      />
    </section>
  )
}
